import { FlowType } from "@redotech/redo-model/return-flow";
import { PriceOption } from "@redotech/redo-model/return-flow/condition";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { PriceState } from "@redotech/redo-return-ui/flows/return-flow/condition-types/price";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

export const PriceDetails = memo(function PriceDetails({
  state,
  setState,
  disabled,
  flowType,
}: ConditionTypeDetailsProps<PriceState>) {
  function handlePriceOptionChange(priceOption: PriceOption) {
    setState({
      ...state,
      priceOption,
      ...(priceOption === PriceOption.CENTS_EQUALS &&
        +state.amount >= 1 && { amount: "0.99" }),
    });
  }
  function handleAmountChange(amount: string) {
    setState({ ...state, amount });
  }

  const dropdownOptionsValues =
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM
      ? Object.values(PriceOption).filter(
          (option) => option !== PriceOption.CENTS_EQUALS,
        )
      : Object.values(PriceOption);

  const priceOptions: RedoListItem<PriceOption>[] = useMemo(
    () =>
      dropdownOptionsValues.map((option) => ({
        id: option,
        value: option,
        type: "text" as const,
        text: option,
      })),
    [dropdownOptionsValues],
  );

  const selectedOption = state.priceOption
    ? priceOptions.find((option) => option.value === state.priceOption)
    : undefined;

  return (
    <>
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label="Comparison"
        options={priceOptions}
        optionSelected={({ item }) => handlePriceOptionChange(item.value)}
        placeholder="Select comparison..."
        selectedItem={selectedOption}
        size="xs"
      />
      {state.priceOption === PriceOption.CENTS_EQUALS ? (
        <RedoTextInput
          IconLeading={() => <>$</>}
          inputTypeHint="number"
          max={0.99}
          min={0}
          onBlur={() => handleAmountChange((+state.amount).toFixed(2))}
          setValue={(e) => handleAmountChange(e)}
          state={disabled ? "readonly" : "default"}
          step={0.01}
          value={state.amount}
        />
      ) : (
        <Flex dir="column" gap="xs">
          <RedoTextInput
            IconLeading={() => <>$</>}
            inputTypeHint="number"
            min={0}
            onBlur={() => handleAmountChange((+state.amount).toFixed(2))}
            placeholder="0.00"
            setValue={(e) => handleAmountChange(e)}
            state={disabled ? "readonly" : "default"}
            value={state.amount}
          />
          {(flowType === FlowType.FINALIZE_RETURN ||
            flowType === FlowType.FINALIZE_CLAIM) && (
            <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
              Total price of all selected items
            </Text>
          )}
        </Flex>
      )}
    </>
  );
});
